<template>
<div class="previewContainer" :style="preview ? 'width: 90%; margin: auto;' : null"> 
    <div class="previewVideoContainer" v-for="video in variables" :key="video.file_id">
        <video class="previewVideo" :ref="'preview-video-' + video.file_id" v-if="video.file_id > 0" v-show="currently_playing == playable_videos.findIndex(vid => vid.file_id == video.file_id)" 
            :src="getVideoAttr(video.file_id, 'src')" autoplay preload loop muted @timeupdate="checkTime(video.file_id, $event.target.currentTime)" @loadeddata="previewLoaded(video.file_id)" />
    </div>
    <div class="controlsContainer" v-show="playable_videos.length > 0">
        <div class="buttonSmall" @click="playPreview" v-show="!preview_playing" v-if="controls">
            <i class="fas fa-play" />
        </div>
        <div class="buttonSmall" @click="pausePreview" v-show="preview_playing" v-if="controls">
            <i class="fas fa-pause" />
        </div>
        <div class="buttonSmall" @click="resetPreview" v-show="!preview_finished" v-if="controls">
            <i class="fas fa-stop" />
        </div>
        <div class="progressSlider" v-show="!preview_finished">
            <VueSlider v-show="!preview_finished" v-model="slider_pos" @change="updateSliderPos" :tooltipFormatter="formatTime" 
                :interval="0.25" :min="0" :max="duration" :width="'100%'" :dotOptions="{tooltip: 'none'}" ref="slider" />
            <div class="sliderDuration" v-show="!preview_finished">
                <div class="sliderDurationLeft">0:00</div>
                <div class="sliderDurationRight">{{ formatTime(duration) }}</div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import Vue from 'vue';
import VueSlider from 'vue-slider-component';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

export default {
    name: 'VideoPreview',
    components: {
        VueSlider
    },
    props: {
        sources: Array,
        preview: Boolean,
        autoplay: Boolean,
        controls: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            variables: this.sources.map(v => ({...v, played: false, loaded: false})),
            currently_playing: 0,
            preview_playing: false,
            preview_finished: true,
            slider_pos: 0
        }
    },
    watch: {
        sources() {
            this.variables = this.sources.map(v => ({...v, played: false, loaded: false}))
        }
    },
    computed: {
        no_sources() { return this.sources.length == 0 },
        playable_videos() { return this.variables.filter(vid => vid.file_id > 0) },
        duration() { return Math.round(this.playable_videos.reduce((acc, item) => acc + (item.range[1] - item.range[0]), 0)) },
        thresholds() { return this.playable_videos.reduce((acc, item, index) => acc.concat((acc.length > 0 ? acc[index - 1] : 0) + (item.range[1] - item.range[0])), [])},
    },
    methods: {
        previewLoaded(file_id) {
            const preview = this.getVideoRef(file_id)
            preview.pause()
            preview.muted = false

            const index = this.variables.findIndex(v => v.file_id == file_id)
            Vue.set(this.variables[index], 'loaded', true)
            if (this.variables.filter(v => v.file_id > 0).every(v => v.loaded)) {
                if (this.autoplay) this.playPreview()
            }
        },
        checkTime(file_id, currentTime) {
            const index = this.variables.findIndex(v => v.file_id == file_id)
            if (this.variables[index].played) return
            const relative_time = currentTime - this.variables[index].range[0]
            this.slider_pos = index > 0 
                ? this.thresholds[index-1] + relative_time
                : relative_time
            
            const preview = this.getVideoRef(file_id)
            if (!preview) return

            const max = this.variables[index].range[1]
            if ((max - currentTime < 0.5) || (currentTime >= max)) {
                preview.pause();
                Vue.set(this.variables[index], 'played', true)
                if (this.playable_videos.length > this.currently_playing + 1) {
                    this.currently_playing += 1
                    this.getVideoRef(this.getFileId(this.currently_playing)).play()
                }
                else this.resetPreview()
            }
        },
        getVideoRef(file_id){
            return this.$refs['preview-video-' + file_id][0]
        },
        getFileId(video_index) {
            return this.playable_videos[video_index].file_id
        },
        playPreview() {
            if (!this.preview_finished) {
                this.resumePreview()
                return
            }

            this.playable_videos.forEach(v => {
                const ref =  this.getVideoRef(v.file_id)
                if (ref.currentTime !== v.range[0]) ref.currentTime = v.range[0]
            })
            this.getVideoRef(this.getFileId(this.currently_playing)).play()
            this.preview_playing = true
            this.preview_finished = false
            this.$emit('disableDrag', true)
        },
        pausePreview() { 
            this.getVideoRef(this.getFileId(this.currently_playing)).pause() 
            this.preview_playing = false
        },
        resumePreview() { 
            this.getVideoRef(this.getFileId(this.currently_playing)).play() 
            this.preview_playing = true
        },
        resetPreview() {
            this.playable_videos.forEach(v => {
                const ref = this.getVideoRef(v.file_id)
                ref.currentTime = v.range[0]
                ref.pause()
            })

            this.variables = this.variables.map(v => ({...v, played: false}))
            this.currently_playing = 0
            this.preview_playing = false
            this.preview_finished = true
            this.$emit('disableDrag', false)
        },
        formatTime(value) {
            const duration = dayjs.duration({seconds: value})
            const minutes = Math.floor(duration.as('minutes'))
            const seconds = Math.floor(duration.as('seconds')) - minutes * 60
            return minutes + ':' + (seconds.toString().length == 1 ? '0' + seconds : seconds)
        },
        getVideoAttr(file_id, attr) { 
            return this.variables.find(v => v.file_id == file_id)[attr] 
        },
        updateSliderPos() {
            const last_threshold_index = this.thresholds.findIndex(t => this.slider_pos >= t)
            const video_index = last_threshold_index + 1
            const curr_ref = this.getVideoRef(this.getFileId(this.currently_playing))
            const vid_ref = this.getVideoRef(this.playable_videos[video_index].file_id)

            this.variables = this.variables.map(v => ({...v, played: v.index >= video_index ? false: true}))
            curr_ref.pause()
            curr_ref.currentTime = this.playable_videos[this.currently_playing].range[0]
            vid_ref.currentTime = this.slider_pos - ( last_threshold_index >= 0 ? this.thresholds[last_threshold_index] : 0) + this.playable_videos[video_index].range[0]
            vid_ref.play()
            this.currently_playing = video_index
        },
    }
}
</script>

<style>

.progressSlider {
    height: 0.5em;
    width: 100%;
}

.sliderDuration {
    display: flex;
    margin-top: 0.5em;
    font-size: 0.9em;
}

.sliderDurationRight {
    margin-left: auto;
}

.controlsContainer {
    display: flex;
    flex-direction: row;
}

.vue-slider {
    padding: 8.5px 0;
}

.vue-slider-rail {
    height: 1em;
    background-color: #7B7B7B !important;
    border-radius: 5px;
}

.vue-slider-process {
    background-color: #FCDBA0 !important;
    border-radius: 0;
}

.vue-slider-dot {
    top: 25% !important;
    width: 0.2em !important;
}

.vue-slider-dot-handle {
    width: 0.2em;
    border-radius: 0;
    height: 1.4em;
    border: none;
    background-color: #F57B2A;
}

.vue-slider-dot-tooltip-inner {
    border: none;
    padding: 2px 8px;
    color: rgba(0, 0, 0, 0.75);
    background-color: transparent;
    box-shadow: none;
}

.vue-slider-dot-tooltip-top {
    top: 44px;
}

.vue-slider-dot-tooltip-inner-top::after {
    display: none;
}

</style>